import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '../../../components/ui/dialog';
import { useOnboarding } from '../hooks/useOnboarding';
import { Monitor, Smartphone, ArrowRight, Zap } from 'lucide-react';

/**
 * Unified welcome modal for new users (Mobile & Web).
 * 
 * SOLID Principles:
 * - Single Responsibility: High-level presentation of the welcome message.
 * - Dependency Inversion: Logic is injected via the useOnboarding hook abstraction.
 * 
 * Layered Architecture: 
 * - This sits in the Presentation Layer (UI Feature components).
 */
export const WelcomeOnboardingModal: React.FC = () => {
  const { showModal, mode, handleDismiss } = useOnboarding();

  if (!showModal) return null;

  const isMobile = mode === 'mobile';

  return (
    <Dialog open={showModal} onOpenChange={(val) => {
      if (!val) handleDismiss();
    }}>
      <DialogContent className="sm:max-w-[425px] w-[90vw] rounded-2xl border-none bg-white p-0 overflow-hidden shadow-2xl">
        {/* Header with Premium Gradient */}
        <div className="bg-gradient-to-br from-[#1F4068] to-[#1CB3B3] p-8 text-center relative">
          {/* Subtle background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-3xl"></div>
          
          <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/30 animate-pulse">
            {isMobile ? (
              <Smartphone className="w-10 h-10 text-white" />
            ) : (
              <Monitor className="w-10 h-10 text-white" />
            )}
          </div>
          
          <DialogTitle className="text-2xl font-bold text-white mb-2 leading-tight">
            {isMobile ? '¡Bienvenido a Reveca Mobile!' : '¡Bienvenido a Reveca!'}
          </DialogTitle>
          
          <DialogDescription className="text-blue-100 text-sm">
            {isMobile 
              ? 'Optimiza tu experiencia siguiendo estas recomendaciones clave.' 
              : 'Configura tu cuenta correctamente para empezar a enviar mensajes.'}
          </DialogDescription>
        </div>

        {/* Content Layers */}
        <div className="p-8 space-y-6">
          {/* Web Recommendation (Mobile Layer Only) */}
          {isMobile && (
            <div className="flex gap-4 items-start group">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1CB3B3]/10 transition-colors">
                <Monitor className="w-6 h-6 text-[#1CB3B3]" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-base mb-1">Mejor en PC</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Para mayor comodidad al configurar tus campañas, te recomendamos usar la versión <span className="font-bold text-gray-900">Web</span> desde un ordenador.
                </p>
              </div>
            </div>
          )}

          {/* SIM Card Requirement (Unified Layer) */}
          <div className="flex gap-4 items-start group">
            <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1CB3B3]/10 transition-colors">
              <Zap className="w-6 h-6 text-[#1CB3B3]" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-base mb-1">Nueva SimCard</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                Recuerda que necesitas una <span className="font-bold text-gray-900">SimCard nueva</span> para conectar tu cuenta a WhatsApp sin riesgos.
              </p>
            </div>
          </div>
        </div>

        {/* Footer with CTA */}
        <DialogFooter className="p-8 pt-0">
          <button
            onClick={handleDismiss}
            className="w-full bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white py-4 px-6 rounded-xl font-bold hover:opacity-90 transition-all duration-200 shadow-[0_10px_20px_-10px_rgba(28,179,179,0.5)] flex items-center justify-center gap-2 group"
          >
            <span>{isMobile ? 'Entendido, ¡vamos allá!' : '¡Listo, empecemos!'}</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
